// rAF edge auto-scroll for pointer-capture gestures that never go through
// dnd-kit (resize, marquee). Near the scroll viewport's top/bottom edge the
// loop scrolls each frame, ramping linearly to max speed at (or past) the
// edge, and calls `onScrolled` after every displacement so the gesture math
// re-runs while the pointer sits parked at the edge. When the scroll position
// stops changing (edge reached) the loop stands down until the next
// `update()`. Lifted verbatim from the resize hook so marquee shares one
// implementation.

import { clamp } from './grid-layout';
import type { Omittable } from "../../lib/props";

export interface EdgeAutoScroller {
  /** Feed the latest pointer clientY; starts/stops the loop as needed. */
  update(pointerClientY: number): void;
  /** Cancel any pending frame (call on gesture end/cancel). */
  stop(): void;
}

export function createEdgeAutoScroller({
  scrollParent,
  onScrolled,
  thresholdPx = 48,
  maxSpeedPx = 15,
}: Omittable<{
  scrollParent: HTMLElement | null;
  onScrolled: () => void;
  thresholdPx?: number;
  maxSpeedPx?: number;
}>): EdgeAutoScroller {
  let pointerY = 0;
  let rafId: number | null = null;

  // Scroll-edge proximity → direction (-1 up / 1 down / 0 none) and speed.
  const scrollVector = (): { dir: number; speed: number } => {
    // Backstop for a direct caller: both gestures feed this from
    // `getScrollParent`, which falls back to `document.scrollingElement` and so
    // never hands over null — no gesture can reach this arm.
    if (!scrollParent) return { dir: 0, speed: 0 };
    const isDocument = scrollParent === document.scrollingElement;
    const rect = isDocument ? null : scrollParent.getBoundingClientRect();
    const viewportTop = rect ? rect.top : 0;
    const viewportBottom = rect ? rect.bottom : window.innerHeight;
    const topDist = pointerY - viewportTop;
    const bottomDist = viewportBottom - pointerY;
    if (topDist < thresholdPx) {
      const d = clamp(topDist, 0, thresholdPx);
      return { dir: -1, speed: maxSpeedPx * (1 - d / thresholdPx) };
    }
    if (bottomDist < thresholdPx) {
      const d = clamp(bottomDist, 0, thresholdPx);
      return { dir: 1, speed: maxSpeedPx * (1 - d / thresholdPx) };
    }
    return { dir: 0, speed: 0 };
  };

  const stop = () => {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const tick = () => {
    rafId = null;
    if (!scrollParent) return;
    const { dir, speed } = scrollVector();
    if (dir === 0) return;
    const before = scrollParent.scrollTop;
    scrollParent.scrollTop = before + dir * speed;
    // Edge reached — the position stopped changing; stand down until the
    // pointer moves again.
    if (scrollParent.scrollTop === before) return;
    // The viewport moved under a parked pointer: re-run the gesture math so
    // the preview keeps tracking while the pointer sits at the edge.
    onScrolled();
    rafId = requestAnimationFrame(tick);
  };

  const update = (pointerClientY: number) => {
    pointerY = pointerClientY;
    const { dir } = scrollVector();
    if (dir !== 0) {
      if (rafId == null) rafId = requestAnimationFrame(tick);
    } else {
      stop();
    }
  };

  return { update, stop };
}
